import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getDISC_DATA,
  getBIGFIVE_DATA,
  getMBTI_DATA,
  getENNEAGRAM_DATA,
  getSCHWARTZ_DATA,
  getVIA_DATA,
} from '../data/explanations';
import { TestIcons, SchwartzIcon } from '../data/testsData';
import { useTranslation } from '../data/useTranslation';
import LanguageSelector from '../components/LanguageSelector';

// Use relative URL in production, env variable in development
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper component to render test icon
const TestIcon = ({ iconKey, className = "w-10 h-10 sm:w-12 sm:h-12" }) => {
  if (iconKey === 'schwartz') {
    return <div className={`${className} text-orange-500`}>{SchwartzIcon}</div>;
  }
  const iconUrl = TestIcons[iconKey];
  if (iconUrl) {
    return <img src={iconUrl} alt={iconKey} className={`${className} object-contain`} />;
  }
  return null;
};

function Results() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [currentAnalysisLang, setCurrentAnalysisLang] = useState(null);
  const [errorType, setErrorType] = useState(null); // 'no_tests' | 'not_found' | 'error'

  useEffect(() => {
    fetchResults();
  }, []);

  // Regenerate analysis when language changes and all tests are complete
  useEffect(() => {
    if (results && results.all_tests_complete && currentAnalysisLang !== null && currentAnalysisLang !== language) {
      regenerateAnalysis();
    }
  }, [language]);

  const fetchResults = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/results/${assessmentId}`);
      if (response.ok) {
        const data = await response.json();
        setResults(data);
        setCurrentAnalysisLang(data.ai_analysis_language || 'fr');
      } else if (response.status === 404) {
        // Vérifier si l'assessment existe mais n'a pas de résultats
        const assessResponse = await fetch(`${BACKEND_URL}/api/assessments/${assessmentId}`);
        if (assessResponse.ok) {
          const assessData = await assessResponse.json();
          const completedTests = JSON.parse(assessData.completed_tests || '[]');
          if (completedTests.length === 0) {
            setErrorType('no_tests');
          } else {
            setErrorType('not_found');
          }
        } else {
          setErrorType('not_found');
        }
      } else {
        setErrorType('error');
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      setErrorType('error');
    } finally {
      setLoading(false);
    }
  };

  const regenerateAnalysis = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/results/${assessmentId}/regenerate?language=${language}`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.ai_analysis) {
          setResults(prev => ({
            ...prev,
            ai_analysis: data.ai_analysis,
            ai_analysis_language: data.language
          }));
          setCurrentAnalysisLang(data.language);
        }
      }
    } catch (error) {
      console.error('Error regenerating analysis:', error);
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-white">{t.common.loading}</p>
        </div>
      </div>
    );
  }

  if (!results) {
    // Messages d'erreur contextuels
    const errorMessages = {
      no_tests: {
        title: language === 'fr' ? 'Aucun test complété' : 'No tests completed',
        message: language === 'fr' 
          ? 'Vous n\'avez pas encore complété de test. Commencez par passer au moins un test pour voir vos résultats.'
          : 'You haven\'t completed any tests yet. Start by taking at least one test to see your results.',
        buttonText: language === 'fr' ? 'Commencer les tests' : 'Start tests',
        buttonAction: () => navigate(`/test-selection?assessment_id=${assessmentId}`)
      },
      not_found: {
        title: language === 'fr' ? 'Résultats non trouvés' : 'Results not found',
        message: language === 'fr'
          ? 'Les résultats demandés n\'existent pas ou ont été supprimés.'
          : 'The requested results do not exist or have been deleted.',
        buttonText: t.results.backToDashboard,
        buttonAction: () => navigate('/dashboard')
      },
      error: {
        title: t.common.error,
        message: language === 'fr'
          ? 'Une erreur s\'est produite lors du chargement des résultats.'
          : 'An error occurred while loading the results.',
        buttonText: t.results.backToDashboard,
        buttonAction: () => navigate('/dashboard')
      }
    };

    const error = errorMessages[errorType] || errorMessages.error;

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center text-white max-w-md px-4">
          <div className="mb-6">
            {errorType === 'no_tests' ? (
              <svg className="w-16 h-16 mx-auto text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            ) : (
              <svg className="w-16 h-16 mx-auto text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            )}
          </div>
          <h2 className="text-2xl font-bold mb-4">{error.title}</h2>
          <p className="text-gray-400 mb-6">{error.message}</p>
          <button
            onClick={error.buttonAction}
            className={`px-6 py-3 rounded-lg font-medium ${
              errorType === 'no_tests' 
                ? 'bg-orange-500 hover:bg-orange-600' 
                : 'bg-gray-700 hover:bg-gray-600'
            }`}
            data-testid="error-action-btn"
          >
            {error.buttonText}
          </button>
        </div>
      </div>
    );
  }

  const { scores, ai_analysis, completed_tests = [], all_tests_complete = false } = results;
  const allTestsCompleted = completed_tests.length >= 6 || all_tests_complete;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 sm:py-12 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-12">
          {allTestsCompleted && (
            <div className="flex justify-center mb-4">
              <div className="bg-gray-800 rounded-xl p-3 sm:p-4 border border-gray-700">
                <span className="text-2xl sm:text-3xl font-bold text-orange-500">BPM</span>
              </div>
            </div>
          )}
          <h1 className="text-2xl sm:text-4xl font-bold mb-2 sm:mb-3" data-testid="results-title">{t.results.title}</h1>
          <p className="text-gray-400 text-sm sm:text-base mb-2">
            {t.results.generatedOn} {new Date(results.created_at).toLocaleDateString()}
          </p>
          
          {/* Progress indicator */}
          {!allTestsCompleted && (
            <div className="max-w-md mx-auto mt-4 bg-orange-900/20 border border-orange-500/30 rounded-lg p-4">
              <p className="text-orange-400 font-semibold mb-2 text-sm sm:text-base">
                ⚠️ {t.results.profileIncomplete} - {completed_tests.length}/6 {t.results.testsCompleted}
              </p>
              <p className="text-xs sm:text-sm text-gray-400 mb-4">
                {t.results.completeForFull}
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 sm:px-6 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg font-semibold transition text-sm sm:text-base"
                data-testid="continue-tests-btn"
              >
                {t.results.continueTests}
              </button>
            </div>
          )}
          
          {allTestsCompleted && (
            <div className="max-w-md mx-auto mt-4 bg-green-900/20 border border-green-500/30 rounded-lg p-4">
              <p className="text-green-400 font-semibold">
                ✅ {t.results.profileComplete}
              </p>
              <p className="text-green-300/80 text-sm mt-1">
                {t.results.readyForMatching}
              </p>
            </div>
          )}
        </div>

        {/* AI Analysis Summary */}
        {ai_analysis && ai_analysis.profil_global && !ai_analysis.profil_global.includes('Complétez tous les tests') && (
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 sm:p-8 mb-8 relative">
            {regenerating && (
              <div className="absolute inset-0 bg-orange-600/80 rounded-xl flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
                  <p className="mt-2 text-white font-semibold">{t.results.regenerating}</p>
                </div>
              </div>
            )}
            <h2 className="text-xl sm:text-2xl font-bold mb-4">{t.results.synthesis}</h2>
            <p className="text-base sm:text-lg mb-4">{ai_analysis.profil_global}</p>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-semibold mb-2">{t.results.dominantTraits} :</p>
                <ul className="list-disc list-inside space-y-1">
                  {ai_analysis.traits_dominants?.map((trait, idx) => (
                    <li key={idx}>{typeof trait === 'object' ? (trait.value || trait.name || trait.description || JSON.stringify(trait)) : trait}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2">{t.results.keyMotivations} :</p>
                <ul className="list-disc list-inside space-y-1">
                  {ai_analysis.motivations_cles?.map((motivation, idx) => (
                    <li key={idx}>{typeof motivation === 'object' ? (motivation.value || motivation.name || motivation.description || JSON.stringify(motivation)) : motivation}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Test Results */}
        <div className="space-y-8 mb-8">
          {/* DISC Results */}
          {scores.disc && (
            <DISCResult scores={scores.disc} t={t} language={language} />
          )}

          {/* Big Five Results */}
          {scores.bigfive && (
            <BigFiveResult scores={scores.bigfive} t={t} language={language} />
          )}

          {/* MBTI Results */}
          {scores.mbti && (
            <MBTIResult scores={scores.mbti} t={t} language={language} />
          )}

          {/* VIA Strengths */}
          {scores.via && (
            <VIAResult scores={scores.via} t={t} language={language} />
          )}

          {/* Schwartz Values */}
          {scores.schwartz && (
            <SchwartzResult scores={scores.schwartz} t={t} language={language} />
          )}

          {/* Enneagram */}
          {scores.enneagram && (
            <EnneagramResult scores={scores.enneagram} t={t} language={language} />
          )}
        </div>

        {/* AI Recommendations for Network */}
        {ai_analysis?.recommandations_relations && (
          <div className="bg-gray-800 rounded-xl p-6 sm:p-8 border border-gray-700 mb-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-2">{t.results.networkRecommendations}</h2>
            <p className="text-gray-400 text-sm mb-6">
              {t.results.networkRecommendationsSubtitle}
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Pour communiquer avec moi */}
              <div className="bg-green-900/20 border border-green-500/30 rounded-xl p-5">
                <h4 className="text-lg font-semibold mb-4 text-green-400 flex items-center gap-2">
                  <span className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm">✓</span>
                  {t.results.communicateWith}
                </h4>
                <p className="text-gray-400 text-xs mb-3 italic">
                  {t.results.communicateWithSubtitle}
                </p>
                <ul className="space-y-3">
                  {ai_analysis.recommandations_relations.pour_communiquer?.map((item, idx) => (
                    <li key={idx} className="flex items-start space-x-3 bg-green-900/30 rounded-lg p-3">
                      <span className="text-green-400 font-bold text-lg">→</span>
                      <span className="text-sm">{typeof item === 'object' ? (item.value || item.name || item.description || JSON.stringify(item)) : item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* À éviter */}
              <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-5">
                <h4 className="text-lg font-semibold mb-4 text-red-400 flex items-center gap-2">
                  <span className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm">✗</span>
                  {t.results.avoid}
                </h4>
                <p className="text-gray-400 text-xs mb-3 italic">
                  {t.results.avoidSubtitle}
                </p>
                <ul className="space-y-3">
                  {ai_analysis.recommandations_relations.a_eviter?.map((item, idx) => (
                    <li key={idx} className="flex items-start space-x-3 bg-red-900/30 rounded-lg p-3">
                      <span className="text-red-400 font-bold text-lg">✗</span>
                      <span className="text-sm">{typeof item === 'object' ? (item.value || item.name || item.description || JSON.stringify(item)) : item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Network-specific recommendations */}
            {ai_analysis.recommandations_reseau && (
              <div className="mt-6 bg-orange-900/20 border border-orange-500/30 rounded-xl p-5">
                <h4 className="text-lg font-semibold mb-4 text-orange-400 flex items-center gap-2">
                  <span className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm">🤝</span>
                  {t.results.networkContribution}
                </h4>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  {ai_analysis.recommandations_reseau.apport_reseau && (
                    <div>
                      <p className="text-orange-300 font-medium mb-2">{t.results.whatIBring}</p>
                      <ul className="space-y-1 text-gray-300">
                        {ai_analysis.recommandations_reseau.apport_reseau.map((item, idx) => (
                          <li key={idx}>• {typeof item === 'object' ? (item.value || item.name || item.description || JSON.stringify(item)) : item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {ai_analysis.recommandations_reseau.recherche_partenaires && (
                    <div>
                      <p className="text-orange-300 font-medium mb-2">{t.results.whatISeek}</p>
                      <ul className="space-y-1 text-gray-300">
                        {ai_analysis.recommandations_reseau.recherche_partenaires.map((item, idx) => (
                          <li key={idx}>• {typeof item === 'object' ? (item.value || item.name || item.description || JSON.stringify(item)) : item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-semibold transition text-sm sm:text-base"
            data-testid="back-btn"
          >
            {t.results.backToDashboard}
          </button>
          
          {!allTestsCompleted && (
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 bg-orange-500 hover:bg-orange-600 rounded-lg font-semibold transition text-sm sm:text-base"
              data-testid="complete-tests-btn"
            >
              {t.results.completeRemaining}
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

// DISC Results Component with colors
function DISCResult({ scores, t, language }) {
  const DISC_DATA = getDISC_DATA(language);
  const { percentages, dominant_style, profile } = scores;
  const dominantData = DISC_DATA.descriptions[dominant_style];
  
  const labels = {
    fr: { dominant: 'Profil dominant', traits: 'Traits caractéristiques', strengths: 'Forces', commStyle: 'Style de communication préféré' },
    en: { dominant: 'Dominant Profile', traits: 'Key traits', strengths: 'Strengths', commStyle: 'Preferred communication style' }
  };
  const l = labels[language] || labels.fr;

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700" data-testid="disc-results">
      <div className="mb-6 flex items-start gap-4">
        <TestIcon iconKey="disc" className="w-12 h-12 flex-shrink-0" />
        <div>
          <h3 className="text-2xl font-bold mb-2">{t.tests.disc.name}</h3>
          <p className="text-gray-400 text-sm">{t.tests.disc.description}</p>
        </div>
      </div>

      {/* DISC Bars with colors */}
      <div className="space-y-4 mb-6">
        {Object.entries(percentages).map(([key, value]) => (
          <div key={key}>
            <div className="flex justify-between mb-1">
              <span className="font-semibold flex items-center">
                <span 
                  className="w-4 h-4 rounded mr-2" 
                  style={{ backgroundColor: DISC_DATA.colors[key] }}
                />
                {DISC_DATA.labels[key]} ({key})
              </span>
              <span style={{ color: DISC_DATA.colors[key] }}>{value}%</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-3">
              <div
                className="h-3 rounded-full transition-all"
                style={{ 
                  width: `${Math.min(value, 100)}%`,
                  backgroundColor: DISC_DATA.colors[key]
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Dominant Profile */}
      <div className="bg-gray-700/50 rounded-lg p-4">
        <p className="text-lg mb-2">
          <span className="text-orange-500 font-semibold">{l.dominant} :</span>{' '}
          <span className="font-bold" style={{ color: DISC_DATA.colors[dominant_style] }}>
            {profile} - {dominantData?.title}
          </span>
        </p>
        <p className="text-sm text-gray-300 mb-2">{dominantData?.subtitle}</p>
        
        {dominantData && (
          <div className="mt-4 space-y-3 text-sm">
            <div>
              <p className="font-semibold text-gray-400">{l.traits} :</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {dominantData.traits.map((trait, idx) => (
                  <span key={idx} className="px-2 py-1 bg-gray-600 rounded-full text-xs">
                    {typeof trait === 'object' ? (trait.value || trait.name || JSON.stringify(trait)) : trait}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="font-semibold text-gray-400">{l.strengths} :</p>
              <p className="text-gray-300">{dominantData.strengths}</p>
            </div>
            <div>
              <p className="font-semibold text-gray-400">{l.commStyle} :</p>
              <p className="text-gray-300">{dominantData.communication}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Big Five Results Component
function BigFiveResult({ scores, t, language }) {
  const BIGFIVE_DATA = getBIGFIVE_DATA(language);
  const { scores: dimensionScores, interpretation } = scores;
  
  const labels = {
    fr: { level: 'Niveau' },
    en: { level: 'Level' }
  };
  const l = labels[language] || labels.fr;

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700" data-testid="bigfive-results">
      <div className="mb-6 flex items-start gap-4">
        <TestIcon iconKey="bigfive" className="w-12 h-12 flex-shrink-0" />
        <div>
          <h3 className="text-2xl font-bold mb-2">{t.tests.bigfive.name}</h3>
          <p className="text-gray-400 text-sm">{t.tests.bigfive.description}</p>
        </div>
      </div>

      <div className="space-y-6">
        {Object.entries(dimensionScores).map(([key, data]) => {
          const fullLabel = BIGFIVE_DATA.fullLabels[key];
          const desc = BIGFIVE_DATA.descriptions[key];
          const level = interpretation[key];
          const isHigh = data.percentage >= 55;
          
          return (
            <div key={key} className="bg-gray-700/30 rounded-lg p-4">
              <div className="flex justify-between mb-2">
                <span className="font-semibold">
                  {fullLabel} ({key})
                </span>
                <span className="text-orange-400 font-bold">{data.percentage}%</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2 mb-3">
                <div
                  className="bg-orange-500 h-2 rounded-full"
                  style={{ width: `${data.percentage}%` }}
                />
              </div>
              <p className="text-sm text-gray-400 mb-2">
                <span className="font-semibold text-white">{l.level} : </span>
                <span className={isHigh ? 'text-green-400' : 'text-blue-400'}>{level}</span>
              </p>
              <p className="text-sm text-gray-300">
                {isHigh ? desc?.high : desc?.low}
              </p>
              <p className="text-xs text-gray-500 mt-2 italic">{desc?.business}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// MBTI Results Component
function MBTIResult({ scores, t, language }) {
  const MBTI_DATA = getMBTI_DATA(language);
  const { type, scores: dimensionScores, description } = scores;
  const typeData = MBTI_DATA.types[type];
  
  const labels = {
    fr: { strengths: 'Forces de ce type', inBusiness: 'En entreprise' },
    en: { strengths: 'Strengths of this type', inBusiness: 'In business' }
  };
  const l = labels[language] || labels.fr;

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700" data-testid="mbti-results">
      <div className="mb-6 flex items-start gap-4">
        <TestIcon iconKey="mbti" className="w-12 h-12 flex-shrink-0" />
        <div>
          <h3 className="text-2xl font-bold mb-2">{t.tests.mbti.name}</h3>
          <p className="text-gray-400 text-sm">{t.tests.mbti.description}</p>
        </div>
      </div>

      {/* Type Display */}
      <div className="text-center mb-6 bg-gradient-to-r from-orange-500/20 to-orange-600/20 rounded-lg p-6">
        <p className="text-5xl font-bold text-orange-500 mb-2">{type}</p>
        <p className="text-xl text-white mb-1">{typeData?.nickname || description}</p>
        <p className="text-gray-400">{typeData?.description}</p>
      </div>

      {/* Dimension Scores */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {Object.entries(dimensionScores).map(([pair, values]) => {
          const letters = pair.split('/');
          const dim1 = MBTI_DATA.dimensions[pair]?.[letters[0]];
          const dim2 = MBTI_DATA.dimensions[pair]?.[letters[1]];
          
          return (
            <div key={pair} className="bg-gray-700/30 rounded-lg p-4">
              <div className="flex justify-between text-sm mb-2">
                <span className={values[letters[0]] > 50 ? 'text-orange-400 font-bold' : 'text-gray-400'}>
                  {dim1?.label} ({letters[0]})
                </span>
                <span className={values[letters[1]] > 50 ? 'text-orange-400 font-bold' : 'text-gray-400'}>
                  {dim2?.label} ({letters[1]})
                </span>
              </div>
              <div className="flex h-2 bg-gray-600 rounded-full overflow-hidden">
                <div 
                  className="bg-orange-500" 
                  style={{ width: `${values[letters[0]]}%` }}
                />
                <div 
                  className="bg-blue-500" 
                  style={{ width: `${values[letters[1]]}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>{values[letters[0]].toFixed(0)}%</span>
                <span>{values[letters[1]].toFixed(0)}%</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Type Details */}
      {typeData && (
        <div className="bg-gray-700/50 rounded-lg p-4">
          <p className="font-semibold text-gray-400 mb-2">{l.strengths} :</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {typeData.strengths?.map((strength, idx) => (
              <span key={idx} className="px-3 py-1 bg-orange-500/20 text-orange-400 rounded-full text-sm">
                {typeof strength === 'object' ? (strength.value || strength.name || JSON.stringify(strength)) : strength}
              </span>
            ))}
          </div>
          <p className="font-semibold text-gray-400 mb-1">{l.inBusiness} :</p>
          <p className="text-gray-300 text-sm">{typeData.inBusiness}</p>
        </div>
      )}
    </div>
  );
}

// VIA Strengths Results Component
function VIAResult({ scores, t, language }) {
  const VIA_DATA = getVIA_DATA(language);
  const { signature_strengths, top_3 } = scores;
  
  const labels = {
    fr: { top3: 'Vos 3 forces principales', signature7: 'Vos 7 forces signature' },
    en: { top3: 'Your top 3 strengths', signature7: 'Your 7 signature strengths' }
  };
  const l = labels[language] || labels.fr;

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700" data-testid="via-results">
      <div className="mb-6 flex items-start gap-4">
        <TestIcon iconKey="via" className="w-12 h-12 flex-shrink-0" />
        <div>
          <h3 className="text-2xl font-bold mb-2">{t.tests.via.name}</h3>
          <p className="text-gray-400 text-sm">{t.tests.via.description}</p>
        </div>
      </div>

      {/* Top 3 Signature Strengths */}
      <div className="mb-6">
        <p className="font-semibold text-orange-400 mb-3">🏆 {l.top3} :</p>
        <div className="grid md:grid-cols-3 gap-4">
          {top_3?.map((strength, idx) => {
            const strengthName = typeof strength === 'object' ? strength.strength : strength;
            return (
              <div key={idx} className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 rounded-lg p-4 text-center">
                <p className="text-2xl mb-2">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}</p>
                <p className="font-bold text-lg">{strengthName}</p>
                <p className="text-sm text-gray-400 mt-2">{VIA_DATA.descriptions[strengthName]}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* All Signature Strengths */}
      <div className="bg-gray-700/30 rounded-lg p-4">
        <p className="font-semibold text-gray-400 mb-3">{l.signature7} :</p>
        <div className="flex flex-wrap gap-2">
          {signature_strengths?.map((strength, idx) => (
            <span 
              key={idx} 
              className="px-3 py-2 bg-green-900/30 text-green-400 rounded-lg text-sm border border-green-500/30"
            >
              {idx + 1}. {typeof strength === 'object' ? strength.name : strength}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// Schwartz Values Results Component
function SchwartzResult({ scores, t, language }) {
  const SCHWARTZ_DATA = getSCHWARTZ_DATA(language);
  const { core_values, ranked } = scores;
  
  const labels = {
    fr: { coreValues: 'Vos 3 valeurs centrales', ranking: 'Classement complet' },
    en: { coreValues: 'Your 3 core values', ranking: 'Full ranking' }
  };
  const l = labels[language] || labels.fr;

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700" data-testid="schwartz-results">
      <div className="mb-6 flex items-start gap-4">
        <TestIcon iconKey="schwartz" className="w-12 h-12 flex-shrink-0" />
        <div>
          <h3 className="text-2xl font-bold mb-2">{t.tests.schwartz.name}</h3>
          <p className="text-gray-400 text-sm">{t.tests.schwartz.description}</p>
        </div>
      </div>

      {/* Core Values */}
      <div className="mb-6">
        <p className="font-semibold text-orange-400 mb-3">🎯 {l.coreValues} :</p>
        <div className="space-y-4">
          {core_values?.map((item, idx) => {
            const valueName = typeof item === 'object' ? item.value : item;
            const valueData = SCHWARTZ_DATA.values[valueName];
            return (
              <div key={idx} className="bg-gradient-to-r from-orange-500/20 to-transparent rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-3">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}</span>
                  <span className="font-bold text-lg">{valueName}</span>
                </div>
                <p className="text-sm text-gray-300">{valueData?.description}</p>
                <p className="text-xs text-gray-500 mt-1 italic">{valueData?.inBusiness}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* All Values Ranked */}
      <div className="bg-gray-700/30 rounded-lg p-4">
        <p className="font-semibold text-gray-400 mb-3">{l.ranking} :</p>
        <div className="flex flex-wrap gap-2">
          {ranked?.slice(0, 10).map((item, idx) => (
            <span 
              key={idx} 
              className={`px-3 py-1 rounded-full text-sm ${
                idx < 3 
                  ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' 
                  : 'bg-gray-600 text-gray-300'
              }`}
            >
              {idx + 1}. {typeof item.value === 'object' ? (item.value.value || item.value.name || JSON.stringify(item.value)) : item.value}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// Enneagram Results Component
function EnneagramResult({ scores, t, language }) {
  const ENNEAGRAM_DATA = getENNEAGRAM_DATA(language);
  const { dominant_type, dominant_score, top_3_types, top_3 } = scores;
  // Support both top_3_types (legacy) and top_3 (new format)
  const topTypes = top_3_types || top_3 || [];
  const typeData = ENNEAGRAM_DATA.types[dominant_type];
  
  const labels = {
    fr: { 
      score: 'Score', 
      mainMotivation: 'Motivation principale', 
      coreFear: 'Peur fondamentale',
      traits: 'Traits caractéristiques',
      inBusiness: 'En entreprise',
      top3: 'Vos 3 types dominants'
    },
    en: { 
      score: 'Score', 
      mainMotivation: 'Main motivation', 
      coreFear: 'Core fear',
      traits: 'Key traits',
      inBusiness: 'In business',
      top3: 'Your top 3 types'
    }
  };
  const l = labels[language] || labels.fr;

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700" data-testid="enneagram-results">
      <div className="mb-6 flex items-start gap-4">
        <TestIcon iconKey="enneagram" className="w-12 h-12 flex-shrink-0" />
        <div>
          <h3 className="text-2xl font-bold mb-2">{t.tests.enneagram.name}</h3>
          <p className="text-gray-400 text-sm">{t.tests.enneagram.description}</p>
        </div>
      </div>

      {/* Dominant Type */}
      <div className="text-center mb-6 bg-gradient-to-r from-orange-500/20 to-orange-600/20 rounded-lg p-6">
        <p className="text-4xl font-bold text-orange-500 mb-2">{dominant_type}</p>
        <p className="text-xl text-white mb-1">{typeData?.name}</p>
        <p className="text-sm text-gray-400">{l.score} : {dominant_score}/5</p>
      </div>

      {/* Type Details */}
      {typeData && (
        <div className="space-y-4 mb-6">
          <div className="bg-gray-700/30 rounded-lg p-4">
            <p className="font-semibold text-green-400 mb-2">✨ {l.mainMotivation} :</p>
            <p className="text-gray-300">{typeData.motivation}</p>
          </div>
          <div className="bg-gray-700/30 rounded-lg p-4">
            <p className="font-semibold text-red-400 mb-2">⚠️ {l.coreFear} :</p>
            <p className="text-gray-300">{typeData.fear}</p>
          </div>
          <div className="bg-gray-700/30 rounded-lg p-4">
            <p className="font-semibold text-gray-400 mb-2">{l.traits} :</p>
            <div className="flex flex-wrap gap-2">
              {typeData.traits?.map((trait, idx) => (
                <span key={idx} className="px-3 py-1 bg-gray-600 rounded-full text-sm">
                  {typeof trait === 'object' ? (trait.value || trait.name || JSON.stringify(trait)) : trait}
                </span>
              ))}
            </div>
          </div>
          <div className="bg-gray-700/30 rounded-lg p-4">
            <p className="font-semibold text-orange-400 mb-2">💼 {l.inBusiness} :</p>
            <p className="text-gray-300">{typeData.inBusiness}</p>
          </div>
        </div>
      )}

      {/* Top 3 Types */}
      <div className="bg-gray-700/30 rounded-lg p-4">
        <p className="font-semibold text-gray-400 mb-3">{l.top3} :</p>
        <div className="flex flex-wrap gap-2">
          {topTypes?.map((item, idx) => {
            // Support multiple formats: {type: X, score: Y} or {type: X, name: Y, score: Z}
            const typeNum = item.type || (Array.isArray(item) ? item[0] : null);
            const typeScore = item.score || item.percentage || (Array.isArray(item) ? item[1] : null);
            const typeName = item.name || ENNEAGRAM_DATA.types[`Type ${typeNum}`]?.name || '';
            return (
              <span 
                key={idx} 
                className={`px-3 py-2 rounded-lg text-sm ${
                  idx === 0 
                    ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' 
                    : 'bg-gray-600 text-gray-300'
                }`}
              >
                Type {typeNum} - {typeName} ({typeof typeScore === 'number' ? typeScore.toFixed(1) : typeScore})
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Results;
